import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApplicationIcon,
  Chip,
  Icon,
  InlineMessage,
  TableSortable,
  Typography,
} from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import { TOKENS } from '../../constants';
import {
  useManagePageDispatch,
  useManagePageState,
} from '../../contexts/ManagePageContext';
import { useAgentList } from '../../hooks/useAgentManage';
import {
  capitalizeFirstLetter,
  formatDateWithTime,
  getCriticalityProps,
  getPlatformLogo,
  getStatusProps,
} from '../../utils/utilManagepage';

import {
  EmptyList,
  EmptyListWithFilters,
  InitialError,
  InitialLoading,
  LlmModelIcon,
  NextPageError,
  NextPageLoader,
} from './ManagePageShared';
import {
  getAgentTableColumns,
  getSortConfigs,
} from './ManagePageTableColumnConfig';
import RowDash from './RowDash';

import './ManagePageTable.css';

export type CriticalityLevel =
  | 'Critical'
  | 'High'
  | 'Medium'
  | 'Low'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | null;
export interface AgentMetadataOwnerInfo {
  ownerId?: string;
  ownerEmail?: string;
  ownerTitle?: string | null;
  ownerDomain?: string;
  ownerDisabled?: boolean;
  ownerFullName?: string;
  ownerAadObjectId?: string;
}

export interface AgentMetadata {
  model: string;
  temperature?: number;
  instructionPrompt?: string;
  ownerInfo?: AgentMetadataOwnerInfo;
}

export interface AgentOwner {
  id: number;
  name: string;
  email: string;
  role: string;
  type: string | null;
  source: string | null;
  rank: number;
}

export interface AgentRow {
  agentId: string;
  name: string;
  description: string | null;
  displayName: string | null;
  url: string | null;
  ingestionMethod: string | null;
  status: string | null;
  environment: string | null;
  agentType: string | null;
  sourceId: string | null;
  platform: string | null;
  framework: string | null;
  version: string | null;
  criticality: CriticalityLevel;
  agentMetadata: AgentMetadata | null;
  owners: AgentOwner[];
  labels: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  lastUsedAt: string | null;
}

export interface ManagePageTableProps {
  selectedAgent: AgentRow | null;
  onRowClick: (agent: AgentRow) => void;
  setIsSidePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ManagePageTable({
  selectedAgent,
  onRowClick,
  setIsSidePanelOpen,
}: ManagePageTableProps) {
  const intl = useIntl();
  const dispatch = useManagePageDispatch();
  const {
    searchText,
    activeFilters,
    isGlobalLoading,
    sort,
    refreshAgent,
    globalDateFormat,
  } = useManagePageState();

  const [agentList, setAgentList] = useState<AgentRow[] | null>(null);
  const [columns, setColumns] = useState(() => getAgentTableColumns(intl));

  const isFilterApplied = useMemo(
    () =>
      searchText.length > 0 ||
      Object.values(activeFilters).some((oa) => oa.length > 0),
    [searchText, activeFilters]
  );

  const {
    refetch: fetchInitial,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    isError,
  } = useAgentList(searchText, activeFilters, sort);

  const loadAgentList = useCallback(
    async (isInit: boolean = false) => {
      try {
        const { data } = isInit ? await fetchInitial() : await fetchNextPage();
        const pages = data?.pages ?? [];
        if (pages.length === 0) {
          throw Error('No pages returned for list');
        }
        const ap = pages.flatMap((page) => page.agents);
        setAgentList(ap);
      } catch {
        // swallow — list-load errors are surfaced via the query's isError state
      }
    },
    [fetchInitial, fetchNextPage]
  );

  const triggerListFetch = useCallback(() => {
    setAgentList(null);
    setIsSidePanelOpen(false);
    loadAgentList(true);
  }, [loadAgentList, setIsSidePanelOpen]);

  useEffect(() => {
    if (isGlobalLoading) {
      return;
    }
    setAgentList(null);
    const debounceTimer = setTimeout(() => {
      triggerListFetch();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchText, activeFilters, sort, isGlobalLoading]);

  useEffect(() => {
    if (refreshAgent !== null) {
      setAgentList((al) => {
        if (al === null) {
          return al;
        }
        return al.map((a) =>
          a.agentId in refreshAgent ? { ...a, ...refreshAgent[a.agentId] } : a
        );
      });
      dispatch({ refreshAgent: null });
    }
  }, [refreshAgent]);

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage || isLoading) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        loadAgentList();
      }
    },
    [hasNextPage, isFetchingNextPage, isLoading, loadAgentList]
  );

  const sortConfigs = useMemo(
    () =>
      getSortConfigs(intl).map((cfg) => ({
        ...cfg,
        sortOptions: cfg.sortOptions.map((opt) => ({
          ...opt,
          isChecked: sort.field === cfg.key && sort.direction === opt.value,
        })),
      })),
    [intl, sort.field, sort.direction]
  );

  const handleSort = useCallback(
    (field: string, direction: string) => {
      dispatch({
        sort: { field, direction: direction as 'asc' | 'desc' },
      });
    },
    [dispatch]
  );

  const tableRows = useMemo(() => {
    if (!agentList) {
      return [];
    }
    return agentList.map((agent) => {
      const { kind: criticalityKind, color: criticalityColor } =
        getCriticalityProps(agent.criticality);
      const { kind: statusKind, color: statusColor } = getStatusProps(
        agent.status
      );
      const businessOwners = agent.owners.filter(
        (o) => o.role === 'Business Owner'
      );

      return {
        agentId: agent.agentId,
        name: (
          <div className='ManagePageTable-nameCell'>
            <ApplicationIcon
              icon='AiAgent'
              iconColor={TOKENS.CF_SAV_MED}
              size='medium'
            />
            <Typography
              kind='h3'
              lineLimit={2}
              tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
              color={TOKENS.CF_SEC_BLD}>
              {agent.name}
            </Typography>
          </div>
        ),
        description: (
          <Typography
            kind='body2'
            lineLimit={2}
            tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
            color={TOKENS.CF_SEC_BLD}>
            {agent.description ?? <RowDash />}
          </Typography>
        ),
        platform: (
          <div className='ManagePageTable-iconTextCell'>
            {agent.platform !== null ? (
              <ApplicationIcon
                logo={getPlatformLogo(agent.platform)}
                size='xSmall'
              />
            ) : null}
            <Typography
              kind='body2'
              lineLimit={2}
              tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
              color={TOKENS.CF_SEC_BLD}>
              {agent.platform ?? <RowDash />}
            </Typography>
          </div>
        ),
        llmModels: (
          <div className='ManagePageTable-iconTextCell'>
            {agent.agentMetadata?.model ? (
              <LlmModelIcon model={agent.agentMetadata.model} />
            ) : null}
            <Typography
              kind='body2'
              lineLimit={2}
              tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
              color={TOKENS.CF_SEC_BLD}>
              {agent.agentMetadata?.model ?? <RowDash />}
            </Typography>
          </div>
        ),
        status: agent.status ? (
          <Chip
            size='small'
            kind={statusKind}
            label={capitalizeFirstLetter(agent.status)}
            color={statusColor}
            isInteractionDisabled
          />
        ) : (
          <RowDash />
        ),
        criticality: agent.criticality ? (
          <Chip
            size='small'
            kind={criticalityKind}
            label={capitalizeFirstLetter(agent.criticality)}
            color={criticalityColor}
            isInteractionDisabled
          />
        ) : (
          <RowDash />
        ),
        ownerIds:
          businessOwners.length > 0 ? (
            <Typography
              kind='body2'
              lineLimit={2}
              tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
              color={TOKENS.CF_SEC_BLD}>
              {businessOwners.map((o) => o.name).join(', ')}
            </Typography>
          ) : (
            <InlineMessage
              text={intl.formatMessage({
                id: '2cscZx',
                defaultMessage: 'No Owner',
                description: 'No Owner',
              })}
              colorTheme='critical'
              size='medium'
              leftIcon={<Icon kind='AlertCriticalOutline' />}
            />
          ),
        createdAt: (
          <Typography
            kind='body2'
            lineLimit={2}
            tooltipConfigs={{ isPortalEnabled: true, maxWidth: '12rem' }}
            color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.createdAt, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        ),
      };
    });
  }, [agentList, globalDateFormat, intl]);

  const selectedRowIndex = useMemo(() => {
    if (!selectedAgent || !agentList) {
      return undefined;
    }
    const i = agentList.findIndex((a) => a.agentId === selectedAgent.agentId);
    return i >= 0 ? i : undefined;
  }, [selectedAgent, agentList]);

  const handleRowClick = useCallback(
    (rowOrIndex: unknown, maybeIndex: unknown) => {
      let index: number | undefined;
      if (typeof maybeIndex === 'number' && maybeIndex >= 0) {
        index = maybeIndex;
      } else if (typeof rowOrIndex === 'number' && rowOrIndex >= 0) {
        index = rowOrIndex;
      } else if (
        rowOrIndex &&
        typeof rowOrIndex === 'object' &&
        'agentId' in rowOrIndex &&
        typeof (rowOrIndex as { agentId: string }).agentId === 'string' &&
        agentList
      ) {
        index = agentList.findIndex(
          (a) => a.agentId === (rowOrIndex as { agentId: string }).agentId
        );
      }
      if (index === undefined || index < 0 || !agentList) {
        return;
      }
      const agent = agentList[index];
      if (agent) {
        onRowClick(agent);
      }
    },
    [agentList, onRowClick]
  );

  if (isError && agentList === null) {
    return <InitialError onRefresh={triggerListFetch} />;
  }
  if (isLoading || agentList === null) {
    return <InitialLoading />;
  }
  if (!isFilterApplied && agentList.length === 0) {
    return (
      <EmptyList
        onNewSetUp={() =>
          (window.location.href = '/ECMv6/intelligence/integrations')
        }
      />
    );
  }
  if (isFilterApplied && agentList.length === 0) {
    return <EmptyListWithFilters />;
  }

  return (
    <div className='ManagePageTable-tableWrapper'>
      <TableSortable
        columnConfigs={{ isDndDisabled: true }}
        columns={columns}
        setColumns={setColumns}
        data={tableRows}
        sortConfigs={sortConfigs}
        onSort={handleSort}
        onScroll={handleTableScroll}
        selectedRowIndex={selectedRowIndex}
        onRowClick={handleRowClick as () => void}
        isVirtualizationEnabled={false}
        isWindowScroll={false}
        tableHeight='min(72vh, calc(100dvh - 14rem))'
        className='ManagePageTable-table'
        dataTestId='ManagePageTable-Table'
      />
      {(hasNextPage || isFetchingNextPage) && <NextPageLoader />}
      {isFetchNextPageError && <NextPageError onNextRefresh={loadAgentList} />}
    </div>
  );
}
